import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../components/modal/modal";

const EmployerSettings = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("id"); // Получаем id из URL

  const [modalMessage, setModalMessage] = useState(null);
  const [view, setView] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    hvhh: "",
    responsibleName: "",
    responsibleSurname: "",
    tel: "",
    email: "",
    currentPassword: "",
    newPassword: "",
  });

  // Загружаем данные работодателя
  useEffect(() => {
    if (!userId) return; // Если id нет, не делаем fetch

    const fetchEmployer = async () => {
      try {
        const res = await fetch(
          `/api/get_user.php?id=${userId}`
        );
        const data = await res.json();

        if (data.success && data.user) {
          setFormData((prev) => ({
            ...prev,
            companyName: data.user.companyName || "",
            hvhh: data.user.hvhh || "",
            responsibleName: data.user.responsibleName || "",
            responsibleSurname: data.user.responsibleSurname || "",
            tel: data.user.tel || "",
            email: data.user.email || "",
          }));
        }
      } catch (err) {
        console.error("Fetch employer error:", err);
      }
    };

    fetchEmployer();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleView = () => setView(!view);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return;

    const formDataObj = new FormData();
    for (const key in formData) {
      formDataObj.append(key, formData[key]);
    }
    formDataObj.append("id", userId);

    try {
      const res = await fetch("/api/update_user.php", {
        method: "POST",
        body: formDataObj,
      });

      const data = await res.json();

      if (data.success) {
        setModalMessage({
          title: t("success"),
          ok: t("ok"),
          text: t("savechangessms"),
          success: true,
        });
        setFormData((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
        }));
        setShowNewPassword(false);
      }else {
        setModalMessage({
          title: t("error"),
          ok: t("ok"),
          text: data.message || t("errortitle"),
          success: false,
        });
      }
    } catch (err) {
      console.error("Update error:", err);
      setModalMessage({
        title: t("error"),
        ok: t("ok"),
        text: t("errortitle"),
        success: false,
      });
    }
  };

  return (
    <section className="h-screen opacity-0 py-[20px] animate-[showtitle_1s_forwards]">
      {modalMessage && (
        <Modal
          title={modalMessage.title}
          ok={modalMessage.ok}
          text={modalMessage.text}
          success={modalMessage.success}
          onClose={() => setModalMessage(null)}
        />
      )}

      <form
        onSubmit={handleSubmit}
        className="w-[500px] max-sm:w-[90%] m-[50px_auto] opacity-0 animate-[showtitle_1s_forwards] flex flex-col gap-3
        rounded-2xl bg-white shadow-2xl shadow-black border-[2px] border-[#006674]"
      >
        <h1 className="font-bold tracking-widest bg-[#006674] py-3 rounded-t-xl text-white w-full text-center uppercase text-3xl max-sm:text-xl">
          {t("settings")}
        </h1>

        <div className="w-full p-5 flex flex-col gap-3">
          {/* Компания */}
          <label className="text-[#006674] max-sm:text-sm">
            {t("employername")}
          </label>
          <input
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
          />

          {/* HVHH */}
          <label className="text-[#006674] max-sm:text-sm">{t("tin")}</label>
          <input
            type="text"
            name="hvhh"
            value={formData.hvhh}
            onChange={handleChange}
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
          />

          {/* Ответственный */}
          <label className="text-[#006674] max-sm:text-sm">
            {t("Nameofresponsibleperson")}
          </label>
          <input
            type="text"
            name="responsibleName"
            value={formData.responsibleName}
            onChange={handleChange}
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
          />

          <label className="text-[#006674] max-sm:text-sm">
            {t("LastNameofresponsibleperson")}
          </label>
          <input
            type="text"
            name="responsibleSurname"
            value={formData.responsibleSurname}
            onChange={handleChange}
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
          />

          {/* Телефон */}
          <label className="text-[#006674] max-sm:text-sm">{t("phone")}</label>
          <input
            type="tel"
            name="tel"
            value={formData.tel}
            onChange={handleChange}
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
          />

          {/* Email */}
          <label className="text-[#006674] max-sm:text-sm">{t("email")}</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
          />

          {/* Смена пароля */}
          <label className="text-[#006674] max-sm:text-sm">
            {t("currentPassword") || "Ներկայիս գաղտնաբառ"}
          </label>
          <input
            type={view ? "text" : "password"}
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
            placeholder="*******"
          />

          {!showNewPassword && (
            <button
              type="button"
              className="py-2 px-4 bg-[#006674] text-white rounded-[30px] mt-3"
              onClick={() => setShowNewPassword(true)}
            >
              {t("changePassword") || "Сменить пароль"}
            </button>
          )}

          {showNewPassword && (
            <>
              <label className="text-[#006674] max-sm:text-sm">
                {t("newpassword") || "Նոր գաղտնաբառ"}
              </label>
              <div className="flex w-full justify-between items-center gap-3">
                <input
                  type={view ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  className="w-full py-2 px-4 outline-none rounded-md border border-gray-400 focus:border-[#006674] focus:shadow-xl"
                />
                <button
                  type="button"
                  onClick={handleView}
                  className="min-w-[40px] min-h-[40px] rounded-full bg-[#006674] text-white"
                >
                  <FontAwesomeIcon icon={view ? faEyeSlash : faEye} />
                </button>
              </div>
            </>
          )}

          <button
            type="submit"
            className="py-2 px-4 w-full mt-3 capitalize bg-[#006674] text-white rounded-[30px] transition-all duration-200 hover:bg-[#848484]"
          >
            {t("saveChanges") || "Պահպանել փոփոխությունները"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default EmployerSettings;
